import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button } from 'antd';

const AutoLogout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const isWarningVisibleRef = useRef(false);
  const warningTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Time configurations
  const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes total inactivity allowed
  const WARNING_DURATION = 1 * 60 * 1000;  // 1 minute warning before logout
  
  // The time of inactivity before we show the warning
  const warningTime = TIMEOUT_DURATION - WARNING_DURATION;

  const clearAllTimers = () => {
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  };

  const logout = () => {
    clearAllTimers();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsWarningVisible(false);
    isWarningVisibleRef.current = false;
    navigate('/login');
  };

  const showWarning = () => {
    setIsWarningVisible(true);
    isWarningVisibleRef.current = true;
    
    let secondsLeft = Math.floor(WARNING_DURATION / 1000);
    setCountdown(secondsLeft);
    
    // Start countdown for the UI
    countdownIntervalRef.current = setInterval(() => {
      secondsLeft -= 1;
      setCountdown(secondsLeft);
      if (secondsLeft <= 0) {
        clearInterval(countdownIntervalRef.current);
      }
    }, 1000);

    // Schedule actual logout
    logoutTimeoutRef.current = setTimeout(() => {
      logout();
    }, WARNING_DURATION);
  };

  const resetTimers = () => {
    clearAllTimers();
    setIsWarningVisible(false);
    isWarningVisibleRef.current = false;

    // Only start timer if authenticated
    const token = localStorage.getItem('token');
    if (!token || location.pathname === '/login') {
      return;
    }

    warningTimeoutRef.current = setTimeout(showWarning, warningTime);
  };

  useEffect(() => {
    resetTimers();

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      // Ignore background activity if the warning modal is currently shown
      if (!isWarningVisibleRef.current) {
        // Reset the warning timer
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
        warningTimeoutRef.current = setTimeout(showWarning, warningTime);
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      clearAllTimers();
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [navigate, location.pathname]);

  return (
    <Modal
      title="Session Expiring Soon"
      open={isWarningVisible}
      closable={false}
      maskClosable={false}
      zIndex={9999}
      footer={[
        <Button key="logout" danger onClick={logout}>
          Logout Now
        </Button>,
        <Button key="stay" type="primary" onClick={resetTimers}>
          Stay Logged In
        </Button>
      ]}
    >
      <p>You have been inactive for a while.</p>
      <p>For your security, you will be automatically logged out in <strong>{countdown}</strong> seconds.</p>
    </Modal>
  );
};

export default AutoLogout;
